import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../features/AppState";
import {
  removeFetcherObjects,
  upsertFetcherObjects,
  upsertFetcherReport,
} from "../../../../features/fetcherSettingsSlice";
import { setDisplayErrorSnackBar } from "../../../../features/userSlice";
import { FetcherObject } from "../helpers/fetcherSettingsDummyData";
import {
  lookupName,
  objectsFor,
  reportTwins,
} from "../helpers/fetcherSettingsResolver";
import { formatDisplayDateTime, nowStamp } from "../helpers/formatters";
import { nextLocalId } from "../helpers/localIds";
import ObjectsPanel from "../ObjectsPanel";
import SaveChangesReviewModal, {
  FieldChange,
  ObjectChange,
} from "../SaveChangesReviewModal";
import CreateInterfaceModal from "../Interfaces/CreateInterfaceModal";

interface Props {
  open: boolean;
  reportId: string;
  mode: "view" | "edit";
  onClose: () => void;
  onSwitchToEdit: () => void;
  onDuplicate: (reportId: string) => void;
  onShowBrands: (reportId: string) => void;
}

interface ReportFieldDraft {
  name: string;
  iface: string;
  ctype_id: string;
  uri: string;
}

const isStagedId = (id: string) => id.startsWith("staged-");

const EditReportModal = ({
  open,
  reportId,
  mode,
  onClose,
  onSwitchToEdit,
  onDuplicate,
  onShowBrands,
}: Props) => {
  const dispatch = useDispatch();
  const { reports, configs, interfaces, objects, collectionTypes } =
    useSelector((state: AppState) => state.fetcherSettings);

  const report = reports.find((row) => row.id === reportId);

  const [draft, setDraft] = useState<ReportFieldDraft | null>(null);
  const [stagedObjects, setStagedObjects] = useState<FetcherObject[]>([]);
  const [markedForDelete, setMarkedForDelete] = useState<string[]>([]);
  const [isInterfaceDialogOpen, setIsInterfaceDialogOpen] = useState(false);
  const [isReviewOpen, setIsReviewOpen] = useState(false);

  const savedObjects = useMemo(
    () => objectsFor(objects, "report_id", reportId),
    [objects, reportId],
  );

  useEffect(() => {
    if (!open || !report) return;
    setDraft({
      name: report.name,
      iface: report.iface,
      ctype_id: report.ctype_id,
      uri: report.uri ?? "",
    });
    setStagedObjects(objectsFor(objects, "report_id", report.id));
    setMarkedForDelete([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reportId]);

  const isReadOnly = mode === "view";

  if (!report || !draft) return null;

  const backendInterface = interfaces.find((row) => row.id === draft.iface);
  const interfaceOptions = [...interfaces]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((row) => ({ value: row.id, label: `${row.name} (${row.id})` }));

  const twins = reportTwins(reports, { ...report, ...draft });
  const clashes = twins.filter((row) => row.name === draft.name);

  const objectChanges: ObjectChange[] = [
    ...stagedObjects
      .filter((row) => isStagedId(row.id))
      .map<ObjectChange>((row) => ({
        kind: "ADD",
        fieldType: row.k,
        fieldValue: row.v,
      })),
    ...stagedObjects
      .filter((row) => {
        if (isStagedId(row.id)) return false;
        const saved = savedObjects.find((other) => other.id === row.id);
        return !!saved && (saved.k !== row.k || saved.v !== row.v);
      })
      .map<ObjectChange>((row) => ({
        kind: "UPDATE",
        fieldType: row.k,
        fieldValue: row.v,
      })),
    ...markedForDelete
      .map((id) => savedObjects.find((row) => row.id === id))
      .filter((row): row is FetcherObject => !!row)
      .map<ObjectChange>((row) => ({
        kind: "DELETE",
        fieldType: row.k,
        fieldValue: row.v,
      })),
  ];

  const fieldChanges: FieldChange[] = [];
  if (draft.name !== report.name) {
    fieldChanges.push({ label: "Name", from: report.name, to: draft.name });
  }
  if (draft.iface !== report.iface) {
    fieldChanges.push({
      label: "Interface",
      from: interfaces.find((row) => row.id === report.iface)?.name ?? "-",
      to: backendInterface?.name ?? "-",
    });
  }
  if (draft.ctype_id !== report.ctype_id) {
    fieldChanges.push({
      label: "Collection type",
      from: lookupName(collectionTypes, report.ctype_id),
      to: lookupName(collectionTypes, draft.ctype_id),
    });
  }
  if (draft.uri !== (report.uri ?? "")) {
    fieldChanges.push({
      label: "URI value",
      from: report.uri ?? "",
      to: draft.uri,
    });
  }

  const isDirty = fieldChanges.length > 0 || objectChanges.length > 0;

  const handleSave = () => {
    // TODO: PUT /aladdin_api/v1/data_collection/fetcher_report/<shared_uid>
    // The body carries objects {create, update, delete} for the touched rows.
    let nextObjectId = Number(nextLocalId(objects));
    const written = stagedObjects
      .filter((row) => {
        if (isStagedId(row.id)) return true;
        const saved = savedObjects.find((other) => other.id === row.id);
        return !!saved && (saved.k !== row.k || saved.v !== row.v);
      })
      .map((row) => ({
        ...row,
        id: isStagedId(row.id) ? String(nextObjectId++) : row.id,
        lvl: "report_id",
        raw: "report_id",
        mid: report.id,
        mod: nowStamp(),
      }));

    if (markedForDelete.length) dispatch(removeFetcherObjects(markedForDelete));
    if (written.length) dispatch(upsertFetcherObjects(written));

    dispatch(
      upsertFetcherReport({
        ...report,
        name: draft.name.trim(),
        iface: draft.iface,
        ctype_id: draft.ctype_id,
        ctype: lookupName(collectionTypes, draft.ctype_id),
        uri: draft.uri.trim(),
        mod: nowStamp(),
      }),
    );
    dispatch(
      setDisplayErrorSnackBar({
        message: `${draft.name.trim()} saved.`,
        type: "success",
      }),
    );
    setIsReviewOpen(false);
    onClose();
  };

  const reportConfigs = configs.filter((row) => row.report === report.id);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: "960px", maxWidth: "96vw" } }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          {isReadOnly ? "View Report" : "Edit Report"}
        </DialogTitle>
        <Typography variant="body2" sx={{ px: 3, pb: 1.5, color: "#00000099" }}>
          {report.name} ({report.id}) · modified{" "}
          {formatDisplayDateTime(report.mod)}
        </Typography>
        <Divider />

        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            px: 3,
            py: 1.5,
            backgroundColor: "#FAFAFC",
            borderBottom: "1px solid #0000001F",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
              {report.brands}
            </Typography>
            {report.brands > 0 ? (
              <Link
                component="button"
                underline="always"
                color="secondary"
                sx={{ fontSize: "12px" }}
                onClick={() => onShowBrands(report.id)}
              >
                brands - view list
              </Link>
            ) : (
              <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
                brands
              </Typography>
            )}
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
              {reportConfigs.length}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
              configs
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
              {report.objects}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
              objects
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
              Interface
            </Typography>
            <Chip size="small" label={backendInterface?.name ?? "-"} />
          </Box>
        </Stack>

        <DialogContent sx={{ px: 3, py: 2 }}>
          {clashes.length > 0 ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              <b>Blocked - 100% duplicate.</b>{" "}
              {clashes.map((row) => `${row.name} (${row.id})`).join(", ")}{" "}
              already exists on this interface with the same collection type.
            </Alert>
          ) : (
            twins.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <b>Possible duplicate.</b>{" "}
                {twins.map((row) => `${row.name} (${row.id})`).join(", ")}{" "}
                matches on interface, collection type and endpoint.
              </Alert>
            )
          )}

          <Stack spacing={2.5}>
            <Typography
              sx={{
                fontSize: "11px",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                color: "#00000099",
                fontWeight: 700,
              }}
            >
              Identity
            </Typography>
            <TextField
              required
              fullWidth
              size="small"
              label="Report Name"
              value={draft.name}
              disabled={isReadOnly}
              onChange={(event) =>
                setDraft({ ...draft, name: event.target.value })
              }
              color="secondary"
            />
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Autocomplete
                sx={{ flex: 1 }}
                size="small"
                options={interfaceOptions}
                value={
                  interfaceOptions.find(
                    (option) => option.value === draft.iface,
                  ) ?? null
                }
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                onChange={(_, value) =>
                  setDraft({ ...draft, iface: value?.value ?? "" })
                }
                disabled={isReadOnly}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Interface"
                    color="secondary"
                  />
                )}
              />
              {!isReadOnly && (
                <Button
                  color="secondary"
                  sx={{ mt: 0.25 }}
                  onClick={() => setIsInterfaceDialogOpen(true)}
                >
                  Create new interface
                </Button>
              )}
            </Stack>
            <TextField
              select
              required
              fullWidth
              size="small"
              label="Collection Type"
              value={draft.ctype_id}
              disabled={isReadOnly}
              onChange={(event) =>
                setDraft({ ...draft, ctype_id: event.target.value })
              }
              color="secondary"
            >
              {collectionTypes.map((type) => (
                <MenuItem
                  key={type.id}
                  value={type.id}
                  disabled={type.used === 0}
                >
                  {type.name}
                  {type.used === 0 ? " - not used" : ""}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              size="small"
              label="URI Value"
              placeholder="optional"
              value={draft.uri}
              disabled={isReadOnly}
              onChange={(event) =>
                setDraft({ ...draft, uri: event.target.value })
              }
              color="secondary"
            />
          </Stack>

          <ObjectsPanel
            ownLabel="This report"
            ownObjects={stagedObjects}
            inheritedLabel={
              backendInterface
                ? `Inherited from Interface - ${backendInterface.name} (${backendInterface.id})`
                : "Inherited"
            }
            inheritedObjects={objectsFor(objects, "interface_id", draft.iface)}
            markedForDelete={markedForDelete}
            readOnly={isReadOnly}
            onAdd={(fieldType, fieldValue) =>
              setStagedObjects((previous) => [
                ...previous,
                {
                  id: `staged-${Date.now()}`,
                  lvl: "report_id",
                  raw: "report_id",
                  mid: report.id,
                  k: fieldType,
                  v: fieldValue,
                  ufr: "0",
                  mod: "",
                },
              ])
            }
            onEdit={(id, fieldType, fieldValue) =>
              setStagedObjects((previous) =>
                previous.map((row) =>
                  row.id === id ? { ...row, k: fieldType, v: fieldValue } : row,
                ),
              )
            }
            onMarkDelete={(id) => {
              if (isStagedId(id)) {
                setStagedObjects((previous) =>
                  previous.filter((row) => row.id !== id),
                );
                return;
              }
              setMarkedForDelete((previous) =>
                previous.includes(id) ? previous : [...previous, id],
              );
            }}
            onUndoDelete={(id) =>
              setMarkedForDelete((previous) =>
                previous.filter((rowId) => rowId !== id),
              )
            }
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {isReadOnly ? (
            <>
              <Chip size="small" label="Read only" />
              <Box sx={{ flex: 1 }} />
              <Button color="secondary" onClick={onClose}>
                Close
              </Button>
              <Button color="secondary" onClick={() => onDuplicate(report.id)}>
                Duplicate
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={onSwitchToEdit}
              >
                Edit
              </Button>
            </>
          ) : (
            <>
              {isDirty && (
                <Typography sx={{ fontSize: "12.5px", color: "#B45309" }}>
                  ● unsaved changes
                  {markedForDelete.length > 0
                    ? ` · ${markedForDelete.length} object to delete`
                    : ""}
                </Typography>
              )}
              <Box sx={{ flex: 1 }} />
              {isDirty && (
                <Button
                  color="secondary"
                  onClick={() => {
                    setDraft({
                      name: report.name,
                      iface: report.iface,
                      ctype_id: report.ctype_id,
                      uri: report.uri ?? "",
                    });
                    setStagedObjects(savedObjects);
                    setMarkedForDelete([]);
                  }}
                >
                  Discard
                </Button>
              )}
              <Button color="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                disabled={!isDirty || clashes.length > 0}
                onClick={() => setIsReviewOpen(true)}
              >
                Save changes
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <CreateInterfaceModal
        open={isInterfaceDialogOpen}
        onClose={() => setIsInterfaceDialogOpen(false)}
        onCreated={(newInterfaceId) => {
          setDraft((previous) =>
            previous ? { ...previous, iface: newInterfaceId } : previous,
          );
          setIsInterfaceDialogOpen(false);
        }}
      />

      <SaveChangesReviewModal
        open={isReviewOpen}
        entityLabel="Report"
        itemName={report.name}
        fieldChanges={fieldChanges}
        objectChanges={objectChanges}
        affectedCount={report.brands}
        affectedDescription="brands whose requests this report builds"
        dependants={reportConfigs.map((config) => ({
          primary: config.name,
          secondary: `${config.used} brands`,
        }))}
        onClose={() => setIsReviewOpen(false)}
        onConfirm={handleSave}
      />
    </>
  );
};

export default EditReportModal;
